import React, { useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useOutletContext } from "react-router-dom";

import { sanitize } from "dompurify";

import { selectSelectedTeam } from "../../../../features/clubhouse/clubhouseSlice";
import {
    selectAgeLevels,
    selectSetting,
} from "../../../../features/global/globalSlice";
import { useAppSelector } from "../../../../hooks/reduxHooks";
import { ITryout } from "../../../../types/api";
import DeleteTryoutsModal from "./components/DeleteTryoutsModal";
import TryoutsForm from "./components/TryoutsForm";
import useTryoutsHandlers from "./hooks/useTryoutsHandlers";
import { IClubhouseTeamTryoutData } from "./types/tryouts";

const ClubhouseTryouts = (): React.JSX.Element => {
    const [show, setShow] = useState<boolean>(false);
    const [sending, setSending] = useState<boolean>(false);

    const selectedTeam = useAppSelector(selectSelectedTeam);
    const ageLevels = useAppSelector(selectAgeLevels);
    const currentYear = parseInt(
        useAppSelector(state =>
            selectSetting(state, "majorybl_current_league_year"),
        )?.setting,
    );

    const pageContent = useAppSelector(state =>
        selectSetting(state, "majorybl_tryout_text"),
    );

    const { setTryoutList, tryout: contextTryout } = useOutletContext()
        ? useOutletContext<{
              setTryoutList: React.Dispatch<React.SetStateAction<ITryout[]>>;
              tryout: ITryout | undefined;
          }>()
        : { setTryoutList: undefined, tryout: undefined };

    const isTryoutDatesPassed = selectedTeam?.tryouts?.dates
        ? selectedTeam.tryouts.dates.every(
              d => new Date() > new Date(`${d.date}T${d.startTime}`),
          )
        : true;

    const currentAgeLevel = useMemo(() => {
        if (!selectedTeam) return null;

        return (
            selectedTeam.ageLevels.find(
                a =>
                    a.year === currentYear &&
                    ["Youth", "High School"].includes(a.season.season),
            ) || null
        );
    }, [ageLevels, selectedTeam]);

    const [tryout, setTryout] = useState<Partial<ITryout> | undefined>(
        contextTryout ||
            (!isTryoutDatesPassed && selectedTeam?.tryouts
                ? selectedTeam?.tryouts
                : {
                      dates: [],
                      description: "",
                      field: undefined,
                      contactName: "",
                      contactEmail: "",
                      contactNumber: "",
                      contactSite: "",
                      age: "",
                  }),
    );

    const methods = useForm<IClubhouseTeamTryoutData>({
        mode: "onChange",
        defaultValues: {
            contactEmail: tryout?.contactEmail || undefined,
            contactName: tryout?.contactName || undefined,
            contactNumber: tryout?.contactNumber || undefined,
            contactSite: tryout?.contactSite || undefined,
            description: tryout?.description || undefined,
            field: tryout?.field
                ? {
                      value: tryout.field.id,
                      label: tryout.field.name,
                  }
                : null,
            dates:
                tryout?.dates && Object.keys(tryout.dates).length !== 0
                    ? tryout.dates.map(d => ({
                          date: new Date(`${d.date}T00:00:00`),
                          startTime: d.startTime
                              ? new Date(`${d.date}T${d.startTime}`)
                              : undefined,
                          endTime: d.endTime
                              ? new Date(`${d.date}T${d.endTime}`)
                              : undefined,
                      }))
                    : [
                          {
                              date: undefined,
                              startTime: undefined,
                              endTime: undefined,
                          },
                      ],
            age: tryout?.age
                ? {
                      value: ageLevels.find(a => a.ageLevel === tryout.age)?.id,
                      label: tryout?.age,
                  }
                : currentAgeLevel
                  ? {
                        value: currentAgeLevel.id,
                        label: currentAgeLevel.ageLevel,
                    }
                  : null,
        },
    });

    const { handleSubmit, handleDelete } = useTryoutsHandlers({
        setShow,
        setSending,
        setTryout,
        setTryoutList,
        methods,
    });

    return (
        <section className="teamTryouts" data-testid="teamTryouts">
            <div
                className="no-bottom-pad"
                dangerouslySetInnerHTML={{
                    __html: sanitize(pageContent?.setting),
                }}
            />
            <FormProvider {...methods}>
                <TryoutsForm
                    sending={sending}
                    handleSubmit={handleSubmit}
                    setShow={setShow}
                    tryout={tryout}
                />
            </FormProvider>
            <DeleteTryoutsModal
                show={show}
                setShow={setShow}
                handleDelete={handleDelete}
            />
        </section>
    );
};

export default ClubhouseTryouts;
